import { useState, useRef, useLayoutEffect, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Upload, FileUp, FolderUp, ChevronRight, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useBuildingStorageQuota } from "@/hooks/buildings/useBuildingStorageQuota";
import { BuildingStorageQuotaError } from "../services/buildingStorageQuota";
import { uploadsBlocked } from "../utils/storagePresentation";
import { STORAGE_ADMISSION_EVENT } from "../utils/storageBatchFeedback";
import { StorageUploadNotice } from "./StorageUploadNotice";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { FolderPickerDialog } from "@/components/chat/SidebarChat/FolderPickerDialog";
import type { PickedFolder } from "@/components/chat/SidebarChat/folderPickerTypes";
import { folderService, isSystemFolderName } from "../services/folderService";
import { useProjects } from "../hooks/useProjects";
import { usePermissions } from "@/hooks/usePermissions";
import { elideFolderPath, folderAncestorPath } from "../types/organize";
import {
  canPickDirectoryWithEmptyFolders,
  collectFolderPaths,
  extractDroppedFiles,
  isEmptySelection,
  pickDirectory,
  selectionFromFileList,
  type UploadSelection,
} from "../utils/uploadSelection";

/** Where an upload lands. `folderId` null = the project root. */
export interface UploadDestination {
  projectId: string;
  folderId: string | null;
}

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  buildingId: string;
  /**
   * The page is scoped to this project: it is the destination's project, and
   * the destination picker offers folders within it only — no project choice.
   */
  scopedProject?: { id: string; name: string } | null;
  /**
   * Where uploads go unless the user changes it, applied on every opening.
   * Omitted at building scope: the building's only or default project's root
   * is used, and failing both the user is asked before anything uploads.
   */
  initialDestination?: UploadDestination | null;
  /** What was dropped on the page before the dialog opened, awaiting a confirm. */
  pendingSelection?: UploadSelection | null;
  onUpload: (selection: UploadSelection, destination: UploadDestination) => void;
}

/** A destination the user picked, plus the path the picker named it by. */
interface PickedDestination extends UploadDestination {
  /** The picker's `/A/B` path, for a folder it has just created that the
   *  tree below has not refetched yet. */
  folderPathHint: string | null;
}

/**
 * The one upload dialog for every /documents tab. It says where the files will
 * land — "Uploading to Project › Folder › Subfolder" — and that line opens the
 * destination picker, so an upload can be redirected before anything is
 * chosen, without first navigating to the folder.
 */
export function UploadModal({
  isOpen,
  onClose,
  buildingId,
  scopedProject,
  initialDestination,
  pendingSelection,
  onUpload,
}: UploadModalProps) {
  const { t, language } = useLanguage();
  const { currentOrganization, isReady } = useOrganizationContext();
  const orgId = isReady ? currentOrganization?.id : undefined;
  const quota = useBuildingStorageQuota(buildingId, orgId);
  const visibleQuota = quota.error instanceof BuildingStorageQuotaError ? undefined : quota.data;
  const blocked = visibleQuota ? uploadsBlocked(visibleQuota) : false;
  const [admissionFailed, setAdmissionFailed] = useState(false);
  const admissionFailedRef = useRef(false);
  const deliveryState = useRef({ blocked, isOpen });
  useLayoutEffect(() => {
    deliveryState.current = { blocked, isOpen };
  }, [blocked, isOpen]);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ buildingId: string; orgId: string }>).detail;
      if (detail.buildingId === buildingId && detail.orgId === orgId) {
        admissionFailedRef.current = true;
        setAdmissionFailed(true);
      }
    };
    window.addEventListener(STORAGE_ADMISSION_EVENT, listener);
    return () => window.removeEventListener(STORAGE_ADMISSION_EVENT, listener);
  }, [buildingId, orgId]);
  const copy = t.documents.uploadModal;
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  // The user's own choice for this opening, cleared on close so the next
  // opening starts from the caller's destination again.
  const [picked, setPicked] = useState<PickedDestination | null>(null);
  // A selection made before any destination existed (building scope with no
  // single or default project), held while the picker asks where it goes.
  const [heldSelection, setHeldSelection] = useState<UploadSelection | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Already free of hidden and archived projects; narrowed to the ones the user
  // may add to, so the fallback never lands an upload where it will be refused.
  const { projects } = useProjects(buildingId);
  const { canWriteFilesInProject } = usePermissions();
  const visibleProjects = useMemo(
    () => projects.filter((project) => canWriteFilesInProject(Number(project.id))),
    [projects, canWriteFilesInProject]
  );

  const fallbackDestination = useMemo<UploadDestination | null>(() => {
    if (scopedProject) return { projectId: scopedProject.id, folderId: null };
    const only = visibleProjects.length === 1 ? visibleProjects[0] : undefined;
    const target = only ?? visibleProjects.find((p) => p.is_default);
    return target ? { projectId: target.id, folderId: null } : null;
  }, [scopedProject, visibleProjects]);

  const destination: UploadDestination | null = picked ?? initialDestination ?? fallbackDestination;

  const projectName =
    scopedProject && destination?.projectId === scopedProject.id
      ? scopedProject.name
      : (visibleProjects.find((p) => p.id === destination?.projectId)?.name ?? "");

  // Same key and raw rows as the Folders tab's useCustomFolders, so the tree is
  // already cached there and refreshed by every folder mutation it makes.
  const destinationProjectId = destination?.projectId;
  const { data: projectFolders = [] } = useQuery({
    queryKey: ["custom-folders", buildingId, destinationProjectId],
    queryFn: () =>
      destinationProjectId ? folderService.getProjectFolders(buildingId, destinationProjectId) : [],
    enabled: isOpen && !!buildingId && !!destinationProjectId && !!destination?.folderId,
  });
  const folderTree = useMemo(
    () => folderService.buildEnhancedFolderTree(projectFolders),
    [projectFolders]
  );

  const destinationFolderId = destination?.folderId ?? null;
  const folderNames = useMemo(() => {
    if (destinationFolderId === null) return [];
    const fromTree = folderAncestorPath(folderTree, Number(destinationFolderId));
    if (fromTree.length > 0) return fromTree;
    const hint = picked?.folderId === destinationFolderId ? picked.folderPathHint : null;
    return hint ? hint.split("/").filter(Boolean) : [];
  }, [destinationFolderId, folderTree, picked]);

  const pathSegments = destination ? [projectName, ...folderNames].filter(Boolean) : [];
  const { head, elided, tail } = elideFolderPath(pathSegments);

  const awaiting =
    heldSelection ??
    (pendingSelection && !isEmptySelection(pendingSelection) ? pendingSelection : null);
  const awaitingFolderCount = awaiting
    ? collectFolderPaths(awaiting.relativePaths, awaiting.directoryPaths).length
    : 0;

  const close = () => {
    admissionFailedRef.current = false;
    setAdmissionFailed(false);
    setPicked(null);
    setHeldSelection(null);
    setIsPickerOpen(false);
    onClose();
  };

  /** Upload to the destination, or hold the selection and ask for one. */
  const deliver = (selection: UploadSelection) => {
    if (
      deliveryState.current.blocked ||
      !deliveryState.current.isOpen ||
      isEmptySelection(selection)
    )
      return;
    if (!destination) {
      setHeldSelection(selection);
      setIsPickerOpen(true);
      return;
    }
    onUpload(selection, { projectId: destination.projectId, folderId: destination.folderId });
    if (!admissionFailedRef.current) close();
  };

  const handlePicked = (folder: PickedFolder) => {
    // Issue Attachments is filled from the issues page only — the Folders tab
    // locks its Upload button there, and a picked destination is no way round.
    const folderName = folder.folderPath?.split("/").filter(Boolean).pop();
    if (folder.folderId && isSystemFolderName(folderName)) {
      toast.error(t.pm.issues.detail.systemFolderLocked);
      return;
    }
    setPicked({
      projectId: folder.projectId,
      folderId: folder.folderId,
      folderPathHint: folder.folderPath,
    });
    setIsPickerOpen(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    // Ours to handle — don't let it bubble to the page's external-file drop
    // zone, which would upload the same files a second time. React propagates
    // synthetic events up the React tree even though the dialog is portalled.
    e.stopPropagation();
    setIsDragOver(false);
    if (blocked) return;

    extractDroppedFiles(e.dataTransfer)
      .then(deliver)
      .catch((error) => {
        console.error("Failed to read dropped files:", error);
        toast.error(copy.readFailed);
      });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selection = selectionFromFileList(e.target.files);
    // Reset so choosing the same files again still fires `change`.
    e.target.value = "";
    deliver(selection);
  };

  const handleSelectFiles = () => {
    if (!blocked) fileInputRef.current?.click();
  };

  const handleSelectFolder = () => {
    if (blocked) return;
    // `<input webkitdirectory>` cannot report empty folders; the File System
    // Access picker can, so use it wherever the browser has it.
    if (!canPickDirectoryWithEmptyFolders()) {
      folderInputRef.current?.click();
      return;
    }
    pickDirectory()
      .then((selection) => {
        if (selection) deliver(selection);
      })
      .catch((error) => {
        console.error("Failed to read the chosen folder:", error);
        toast.error(copy.readFailed);
      });
  };

  // The last segment is where the files land, so it keeps its full name the
  // longest; the segments above it truncate first. No ring or box on focus:
  // the dialog focuses this line on open, and keyboard focus underlines
  // "Change" instead.
  const tailOffset = head.length + (elided.length > 0 ? 1 : 0);
  const lastSegmentIndex = tailOffset + tail.length - 1;
  const renderSegment = (name: string, key: string, index: number) => (
    <span
      key={key}
      className={`flex min-w-0 items-center gap-1 ${index === lastSegmentIndex ? "max-w-full shrink-0" : ""}`}
    >
      {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="truncate">{name}</span>
    </span>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="sm:max-w-md"
        // Drops landing outside the drop zone still belong to this dialog, so
        // swallow them rather than letting the page treat them as an upload.
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragOver={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>

        {/* min-w-0: DialogContent is a grid, and a long destination path would
            otherwise widen its column past the dialog instead of truncating. */}
        <div className="min-w-0 space-y-4">
          {visibleQuota && <StorageUploadNotice quota={visibleQuota} buildingId={buildingId} />}
          {admissionFailed && !blocked && (
            <p role="alert" className="text-sm text-destructive">
              {language === "et"
                ? "Neid faile ei õnnestunud salvestusruumi lisada. Kontrolli vaba ruumi ja proovi uuesti."
                : "These files could not be admitted to storage. Check the available space and try again."}
            </p>
          )}
          {/* Where the upload lands; opens the destination picker. */}
          <button
            type="button"
            onClick={() => setIsPickerOpen(true)}
            className="group flex w-full items-center gap-2 text-left text-sm focus-visible:outline-none"
            title={pathSegments.join(" › ") || undefined}
            data-testid="upload-destination"
          >
            <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
            {destination ? (
              <>
                <span className="shrink-0 text-muted-foreground">{copy.uploadingTo}</span>
                <span className="flex min-w-0 flex-1 items-center gap-1 font-medium">
                  {head.map((name, i) => renderSegment(name, `h${i}`, i))}
                  {elided.length > 0 && renderSegment("…", "elided", head.length)}
                  {tail.map((name, i) => renderSegment(name, `t${i}`, tailOffset + i))}
                </span>
              </>
            ) : (
              <span className="flex-1 font-medium">{copy.chooseDestination}</span>
            )}
            <span className="shrink-0 text-xs font-medium text-primary group-hover:underline group-focus-visible:underline">
              {copy.changeDestination}
            </span>
          </button>

          {awaiting ? (
            <div className="space-y-3">
              <div className="space-y-1 rounded-lg border bg-muted/50 p-3 text-sm">
                {awaiting.files.length > 0 && (
                  <p>{copy.filesReady.replace("{count}", String(awaiting.files.length))}</p>
                )}
                {awaitingFolderCount > 0 && (
                  <p>{copy.foldersReady.replace("{count}", String(awaitingFolderCount))}</p>
                )}
              </div>
              <Button
                size="sm"
                className="w-full"
                disabled={!destination || blocked}
                onClick={() => deliver(awaiting)}
              >
                {copy.upload}
              </Button>
            </div>
          ) : (
            // Non-modal: the dialog already traps focus, and a modal menu also
            // locks `body` pointer-events. "Upload Folder" can close the dialog
            // while the menu is still closing (the picker may resolve at once),
            // and Radix then leaves `body` locked — a dead page.
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild disabled={blocked}>
                <div
                  className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                    isDragOver
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-primary hover:bg-primary/5"
                  }`}
                  data-testid="upload-drop-zone"
                  aria-disabled={blocked}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                >
                  <Upload className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="mb-2 text-lg font-medium">
                    {isDragOver ? copy.dropFilesHere : copy.dragDropFiles}
                  </p>
                  <p className="text-sm text-muted-foreground">{copy.orClickToBrowse}</p>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-44">
                <DropdownMenuItem
                  onClick={handleSelectFiles}
                  className="cursor-pointer gap-3 px-3 py-2.5"
                >
                  <FileUp className="h-4 w-4 text-muted-foreground" />
                  <span>{copy.uploadFiles}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSelectFolder}
                  className="cursor-pointer gap-3 px-3 py-2.5"
                >
                  <FolderUp className="h-4 w-4 text-muted-foreground" />
                  <span>{copy.uploadFolder}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {blocked && !awaiting && (
            <Button type="button" size="sm" className="w-full" disabled>
              {copy.upload}
            </Button>
          )}
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            webkitdirectory=""
          />
        </div>
      </DialogContent>

      {/* Mounted only while open: the picker remembers its last pick across
          openings, but it must always open on the destination this dialog
          shows — which a close here may have reset. */}
      {isPickerOpen && (
        <FolderPickerDialog
          open
          onOpenChange={(open) => {
            setIsPickerOpen(open);
            // A selection held for a destination the user then declined to
            // give has nowhere to go; drop it rather than keep a dead confirm up.
            if (!open && !destination) setHeldSelection(null);
          }}
          buildingId={buildingId}
          initialProjectId={destination?.projectId ?? scopedProject?.id}
          initialFolderId={destination?.folderId ?? null}
          lockProject={!!scopedProject}
          title={copy.destinationTitle}
          description={copy.destinationDescription}
          submitLabel={copy.destinationSubmit}
          onSelect={handlePicked}
        />
      )}
    </Dialog>
  );
}
